import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, XCircle, Loader2, FileText, Building2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const money = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const InvoiceCard = ({ inv, token, email, onDecided }) => {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const fileUrl = `${BACKEND}/api/public/invoice-approval/${token}/file/${inv.id}`;
  const oa = inv.owner_approval;
  const decide = async (decision) => {
    if (!email) return toast.error("Choose your email address at the top first");
    setBusy(true);
    try {
      await axios.post(`${BACKEND}/api/public/invoice-approval/${token}/decide`, { invoice_id: inv.id, decision, email, note });
      toast.success(decision === "approved" ? "Approved" : "Declined");
      onDecided();
    } catch (e) { toast.error(e.response?.data?.detail || "Something went wrong"); }
    finally { setBusy(false); }
  };
  return (
    <div className="grid gap-4 border border-border bg-card p-4 md:grid-cols-[1fr_340px]" data-testid={`public-invoice-${inv.id}`}>
      <div className="min-h-[360px] border border-border bg-muted/30">
        {inv.has_file ? (inv.content_type || "").startsWith("image/")
          ? <img src={fileUrl} alt="Invoice" className="h-[480px] w-full object-contain" />
          : <iframe src={fileUrl} title={`Invoice ${inv.invoice_number}`} className="h-[480px] w-full" />
          : <div className="flex h-full items-center justify-center text-sm text-muted-foreground"><FileText className="mr-2 h-4 w-4" /> No file attached</div>}
      </div>
      <div className="flex flex-col gap-3">
        <div>
          <div className="text-lg font-bold">{inv.vendor_name}</div>
          <div className="text-sm text-muted-foreground">Invoice #{inv.invoice_number || "—"} · {inv.invoice_date}</div>
          <div className="mt-2 font-mono text-2xl font-black">{money(inv.amount)}</div>
          {inv.memo && <div className="mt-1 text-sm">{inv.memo}</div>}
          {inv.has_file && <a href={fileUrl} target="_blank" rel="noreferrer" className="mt-1 inline-block text-xs text-primary underline">Open file in new tab</a>}
        </div>
        {inv.deleted ? <div className="text-sm font-semibold text-muted-foreground">This invoice was removed.</div> : (
          <>
            {oa && (
              <div className={`flex items-center gap-1.5 text-sm font-semibold ${oa.status === "approved" ? "text-emerald-600" : "text-red-600"}`} data-testid={`public-invoice-status-${inv.id}`}>
                {oa.status === "approved" ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />} {oa.status === "approved" ? "Approved" : "Declined"} by {oa.by}{oa.note ? ` — ${oa.note}` : ""}
              </div>
            )}
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (required to decline)" data-testid={`public-invoice-note-${inv.id}`} />
            <div className="flex gap-2">
              <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" disabled={busy} onClick={() => decide("approved")} data-testid={`public-invoice-approve-${inv.id}`}>
                <CheckCircle2 className="mr-1.5 h-4 w-4" /> {oa ? "Approve" : "Approve to pay"}
              </Button>
              <Button variant="outline" className="flex-1 border-red-400 text-red-700" disabled={busy} onClick={() => decide("declined")} data-testid={`public-invoice-decline-${inv.id}`}>
                <XCircle className="mr-1.5 h-4 w-4" /> Decline
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default function InvoiceApprovalPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [email, setEmail] = useState("");
  const load = () => axios.get(`${BACKEND}/api/public/invoice-approval/${token}`)
    .then((r) => { setData(r.data); if (r.data.recipients.length === 1) setEmail(r.data.recipients[0]); })
    .catch((e) => setErr(e.response?.data?.detail || "This link is not valid"));
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  if (err) return <div className="flex min-h-screen items-center justify-center p-6 text-center" data-testid="public-approval-error"><div><div className="text-2xl font-black">Link unavailable</div><p className="mt-2 text-muted-foreground">{err}</p></div></div>;
  if (!data) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  const total = data.invoices.filter((i) => !i.deleted).reduce((s, i) => s + Number(i.amount || 0), 0);
  const done = data.invoices.filter((i) => i.owner_approval).length;
  return (
    <div className="min-h-screen bg-background" data-testid="public-approval-page">
      <Toaster richColors position="top-center" />
      <div className="mx-auto max-w-5xl space-y-5 px-4 py-8">
        <div className="flex flex-wrap items-end justify-between gap-4 border-b border-border pb-5">
          <div>
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-primary"><Building2 className="h-4 w-4" /> GCG Hotels · Invoice Approval</div>
            <h1 className="mt-1 font-heading text-3xl font-black">{data.property_name}</h1>
            <p className="mt-2 whitespace-pre-line text-sm">{data.message}</p>
            <p className="mt-1 text-xs text-muted-foreground">Sent by {data.sent_by} · {data.invoices.length} invoice(s) · {money(total)} · {done} decided</p>
          </div>
          <div className="w-72 space-y-1">
            <div className="text-xs font-semibold">I am</div>
            <Select value={email} onValueChange={setEmail}>
              <SelectTrigger data-testid="public-approval-email"><SelectValue placeholder="Choose your email" /></SelectTrigger>
              <SelectContent>{data.recipients.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        {data.invoices.map((inv) => <InvoiceCard key={inv.id} inv={inv} token={token} email={email} onDecided={load} />)}
      </div>
    </div>
  );
}
